// Exploration utilities for handling question normalization and exploration management

/**
 * Normalize a question for consistent matching
 * @param {string} question - The question to normalize
 * @returns {string} - Normalized question
 */
export function normalizeQuestion(question) {
  return question.toLowerCase().replace(/[?!.]/g, '').trim();
}

/**
 * Generate a clean title from a question (used by the existing autosave system)
 * @param {string} question - The original question
 * @returns {string} - Clean title for workflow
 */
export function generateTitleFromQuestion(question) {
  // Clean the question and truncate to 40 characters, but preserve the natural question format
  let title = question
    .replace(/[?!]/g, "") // Remove punctuation
    .trim();

  // Capitalize first letter if it's not already
  if (title.length > 0) {
    title = title.charAt(0).toUpperCase() + title.slice(1);
  }

  if (title.length > 40) {
    // Cut at word boundary if possible
    const lastSpace = title.lastIndexOf(" ", 37);
    title =
      lastSpace > 20
        ? title.substring(0, lastSpace) + "..."
        : title.substring(0, 37) + "...";
  }

  return title || "Bible Study Session";
}

/**
 * Check if two questions should be considered the same exploration
 * @param {string} question1 - First question
 * @param {string} question2 - Second question
 * @returns {boolean} - True if they match
 */
export function questionsMatch(question1, question2) {
  return normalizeQuestion(question1) === normalizeQuestion(question2);
}

/**
 * Find an existing workflow that matches a question
 * @param {string} question - The question to check
 * @param {Array} workflows - Array of existing workflows
 * @returns {Object|null} - Matching workflow or null
 */
export function findMatchingWorkflow(question, workflows) {
  const normalizedQuestion = normalizeQuestion(question);
  const expectedTitle = generateTitleFromQuestion(question);

  console.log('🔍 findMatchingWorkflow called');
  console.log('  Question:', question);
  console.log('  Expected title:', expectedTitle);
  console.log('  Total workflows:', workflows.length);

  // First try to match by normalized title
  const titleMatch = workflows.find(workflow => {
    const workflowTitle = normalizeQuestion(workflow.name);
    const expectedTitleNormalized = normalizeQuestion(expectedTitle);
    const matches = workflowTitle === expectedTitleNormalized;

    if (matches) {
      console.log('  ✅ Title match found:', workflow.name);
    }

    return matches;
  });

  if (titleMatch) {
    return titleMatch;
  }

  // Then try to match by first node question (if available)
  return workflows.find(workflow => {
    // Check IndexedDB workflows (have nodes array)
    if (workflow.nodes && workflow.nodes.length > 0) {
      // Only check the FIRST node (index 0), not all nodes
      const firstNode = workflow.nodes[0];
      if (firstNode?.data?.question) {
        const firstNodeQuestion = normalizeQuestion(firstNode.data.question);
        const matches = firstNodeQuestion === normalizedQuestion;

        if (matches) {
          console.log('  ✅ IndexedDB node match found:', workflow.name);
        }

        return matches;
      }
    }

    // Check Firestore workflows (have _firestoreNodes map)
    if (workflow._firestoreNodes) {
      const nodesMap = workflow._firestoreNodes;
      const nodesList = Object.values(nodesMap);

      if (nodesList.length > 0) {
        // Find the root node (the one without a parent or the oldest one)
        // Typically the first node created would be the root question
        const rootNode = nodesList.find(node => node.question && !node.parentId) || nodesList[0];
        if (rootNode?.question) {
          const firstNodeQuestion = normalizeQuestion(rootNode.question);
          const matches = firstNodeQuestion === normalizedQuestion;

          if (matches) {
            console.log('  ✅ Firestore node match found:', workflow.name);
          }

          return matches;
        }
      }
    }

    return false;
  });
}

/**
 * Check if an exploration exists for a question using the existing storage system
 * @param {string} question - The question to check
 * @param {string} userId - Optional user ID to check Firestore (if authenticated)
 * @returns {Promise<boolean>} - True if exploration exists
 */
export async function explorationExists(question, userId = null) {
  try {
    console.log('🔍 explorationExists called');
    console.log('  Question:', question);
    console.log('  userId:', userId);

    let workflows = [];

    // Check Firestore if user is authenticated
    if (userId) {
      try {
        const { getAllWorkflowsFromFirestore } = await import('../services/firestoreService.js');
        const firestoreWorkflows = await getAllWorkflowsFromFirestore(userId);
        console.log('  Firestore workflows loaded:', firestoreWorkflows.length);
        workflows = [...workflows, ...firestoreWorkflows];
      } catch (error) {
        console.error('Failed to load Firestore workflows:', error);
      }
    }

    // Check IndexedDB
    try {
      const storageService = await import('../services/storageService.js');
      const indexedDBWorkflows = await storageService.default.getAllWorkflows();
      console.log('  IndexedDB workflows loaded:', indexedDBWorkflows.length);
      workflows = [...workflows, ...indexedDBWorkflows];
    } catch (error) {
      console.error('Failed to load IndexedDB workflows:', error);
    }

    console.log('  Total workflows to check:', workflows.length);

    const matchingWorkflow = findMatchingWorkflow(question, workflows);

    if (!matchingWorkflow) {
      console.log('  ❌ No matching workflow found');
      return false;
    }

    // Check if workflow has at least one node
    // IndexedDB workflows have nodes array
    // Firestore workflows have nodeCount
    const hasEnoughNodes =
      (matchingWorkflow.nodes && matchingWorkflow.nodes.length >= 1) ||
      (matchingWorkflow.nodeCount && matchingWorkflow.nodeCount >= 1) ||
      (matchingWorkflow._firestoreNodes && Object.keys(matchingWorkflow._firestoreNodes).length >= 1);

    console.log('  Matching workflow:', matchingWorkflow.name);
    console.log('  Has enough nodes:', hasEnoughNodes, {
      nodesLength: matchingWorkflow.nodes?.length,
      nodeCount: matchingWorkflow.nodeCount,
      firestoreNodesCount: matchingWorkflow._firestoreNodes ? Object.keys(matchingWorkflow._firestoreNodes).length : 0
    });

    return hasEnoughNodes;
  } catch (error) {
    console.error('Failed to check if exploration exists:', error);
    return false;
  }
}

/**
 * Load exploration from the existing storage system
 * @param {string} question - The question to look up
 * @param {string} userId - Optional user ID to check Firestore (if authenticated)
 * @returns {Promise<Object|null>} - Exploration data or null if not found
 */
export async function loadExploration(question, userId = null) {
  try {
    console.log('📥 loadExploration called');
    console.log('  Question:', question);
    console.log('  userId:', userId);

    let workflows = [];

    // Check Firestore if user is authenticated
    if (userId) {
      try {
        const { getAllWorkflowsFromFirestore } = await import('../services/firestoreService.js');
        const firestoreWorkflows = await getAllWorkflowsFromFirestore(userId);
        console.log('  Firestore workflows loaded:', firestoreWorkflows.length);
        workflows = [...workflows, ...firestoreWorkflows];
      } catch (error) {
        console.error('Failed to load Firestore workflows:', error);
      }
    }

    // Check IndexedDB
    try {
      const storageService = await import('../services/storageService.js');
      const indexedDBWorkflows = await storageService.default.getAllWorkflows();
      console.log('  IndexedDB workflows loaded:', indexedDBWorkflows.length);
      workflows = [...workflows, ...indexedDBWorkflows];
    } catch (error) {
      console.error('Failed to load IndexedDB workflows:', error);
    }

    console.log('  Total workflows to search:', workflows.length);

    const matchingWorkflow = findMatchingWorkflow(question, workflows);

    if (matchingWorkflow) {
      console.log('  ✅ Found matching workflow:', matchingWorkflow.name);
    } else {
      console.log('  ❌ No matching workflow found');
    }

    return matchingWorkflow;
  } catch (error) {
    console.error('Failed to load exploration:', error);
    return null;
  }
}